import { createSelector } from 'reselect';
import { State, PhysicianInfo, PhysiciansState } from 'types';
import { getAppointmentTypeForUrl } from 'modules/appointmentType';
import { AppointmentType } from 'EntityTypes';

type GetPhysicianProps = {
  physicianId: string | number;
};

const _getPhysicians = (state: State): PhysiciansState => state.entities.physicians;

const sortPhysicians = (physicians: PhysiciansState): PhysicianInfo[] =>
  Object.values(physicians).sort((a: PhysicianInfo, b: PhysicianInfo) =>
    a.firstName.localeCompare(b.firstName),
  );

export const getPhysicians = createSelector(
  _getPhysicians,
  (physicians: PhysiciansState): PhysicianInfo[] => (physicians ? sortPhysicians(physicians) : []),
);

/**
 * Returns an array of bookable physicians, where "bookable" means there is at least one appointment
 * type that allows patients to book that physician.
 */
export const getBookablePhysicians = createSelector(
  getPhysicians,
  (state: State) => state.entities.appointmentTypes,
  (physicians, appointmentTypesState) => {
    if (!physicians || !appointmentTypesState) return [];

    const bookablePhysicianIds = new Set<number>();
    Object.values(appointmentTypesState).forEach((appointmentType) => {
      appointmentType.bookablePhysicianIds.forEach((physicianId) =>
        bookablePhysicianIds.add(physicianId),
      );
    });

    return physicians.filter((physician) => bookablePhysicianIds.has(physician.id));
  },
);

export const getPhysician = (state: State, { physicianId }: GetPhysicianProps): PhysicianInfo =>
  state.entities.physicians ? state.entities.physicians[String(physicianId)] : undefined;

export const getPhysiciansForAppointmentUrl = createSelector(
  getAppointmentTypeForUrl,
  (state: State): PhysiciansState => state.entities.physicians,
  (appointmentType: AppointmentType, physicians: PhysiciansState): PhysicianInfo[] => {
    if (!(appointmentType && physicians)) {
      return undefined;
    }
    return appointmentType.bookablePhysicianIds
      .map((physicianId): PhysicianInfo => physicians[physicianId])
      .filter((physician): boolean => Boolean(physician));
  },
);
